import React, { useState, useEffect } from 'react'
import {
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Autocomplete,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { Helmet } from 'react-helmet-async'
import PageTitleWrapper from '../../../../src/components/PageTitleWrapper'
import PageContainerWrapper from '../../../../src/components/PageContainerWrapper'
import TnCHeader from './PageHeader'
import {
  getStoreTnc,
  saveStoreTnc,
  InvoiceFont,
  FontSizeDeviation,
} from './Services'
import { showAlertAction } from '../../../../src/store/alerts'
import { useAppDispatch, useAppSelector } from '../../../../src/hooks'
import CustomNumberField from '../Users/profile/CustomNumberField'

const NO_DIGIT_OPTIONS = [0, 2, 3, 4, 5, 6] as const
const DEFAULT_NO_DIGITS = 4
const DEFAULT_SALE_INVOICE_PREFIX = 'SL'
const FONT_OPTIONS: readonly InvoiceFont[] = ['FONT_1', 'FONT_2']
const DEFAULT_SELECTED_FONT: InvoiceFont = 'FONT_1'
const DEFAULT_FONT_SIZE_DEVIATION: FontSizeDeviation = 0
const FONT_SIZE_DEVIATION_VALUES: readonly FontSizeDeviation[] = [
  1, 0.5, 0, -0.5, -1,
]
const FONT_SIZE_DEVIATION_OPTIONS: {
  value: FontSizeDeviation
  label: string
}[] = [
  { value: 1, label: 'VERY LARGE' },
  { value: 0.5, label: 'LARGE' },
  { value: 0, label: 'MEDIUM' },
  { value: -0.5, label: 'SMALL' },
  { value: -1, label: 'VERY SMALL' },
]

function parseSelectedFont(value: unknown): InvoiceFont {
  if (value === 'FONT_1' || value === 'FONT_2') {
    return value
  }
  return DEFAULT_SELECTED_FONT
}

function parseFontSizeDeviation(value: unknown): FontSizeDeviation {
  if (value === undefined || value === null) {
    return DEFAULT_FONT_SIZE_DEVIATION
  }
  const n = Number(value)
  const match = FONT_SIZE_DEVIATION_VALUES.find(
    (v) => Math.abs(v - n) < 0.001
  )
  return match ?? DEFAULT_FONT_SIZE_DEVIATION
}

function padSequenceNumber(n: number, digitCount: number): string {
  if (digitCount <= 0) return String(n)
  return String(n).padStart(digitCount, '0')
}

const TnCView: React.FC = () => {
  const [bankAccountDetails, setBankAccountDetails] = useState('')
  const [invoiceTnc, setInvoiceTnc] = useState('')
  const [saleInvoiceTnc, setSaleInvoiceTnc] = useState('')
  const [challanTnc, setChallanTnc] = useState('')
  const [agreementTnc, setAgreementTnc] = useState('')
  const [quotationTnc, setQuotationTnc] = useState('')
  const [nonGstBankAccount, setNonGstBankAccount] = useState('')
  const [invoiceTitle, setInvoiceTitle] = useState('')
  const [id, setId] = useState(null)

  // New fields moved from Software Settings
  const [outwardChallanPrefix, setOutwardChallanPrefix] = useState<string>('')
  const [inwardChallanPrefix, setInwardChallanPrefix] = useState<string>('')
  const [invoiceNoPrefix, setInvoiceNoPrefix] = useState<string>('')
  const [invoiceNoLength, setInvoiceNoLength] = useState<number>(DEFAULT_NO_DIGITS)
  const [saleInvoiceNoPrefix, setSaleInvoiceNoPrefix] = useState<string>(
    DEFAULT_SALE_INVOICE_PREFIX
  )
  const [saleInvoiceNoLength, setSaleInvoiceNoLength] =
    useState<number>(DEFAULT_NO_DIGITS)
  const [challanNoLength, setChallanNoLength] = useState<number>(DEFAULT_NO_DIGITS)
  const [upiId, setUpiId] = useState<string>('')
  const [upiId2, setUpiId2] = useState<string>('')
  const [sgst, setSgst] = useState<number>(9)
  const [cgst, setCgst] = useState<number>(9)
  const [challanFormat, setChallanFormat] = useState<string>('FORMAT_2027')
  const [ironRate, setIronRate] = useState<number | ''>('')
  const [selectedFont, setSelectedFont] =
    useState<InvoiceFont>(DEFAULT_SELECTED_FONT)
  const [fontSizeDeviation, setFontSizeDeviation] = useState<FontSizeDeviation>(
    DEFAULT_FONT_SIZE_DEVIATION
  )

  const dispatchAction = useAppDispatch()
  const { data } = useAppSelector((state) => state.userData)

  // Helper functions for generating example text (padded sequence + full no.)
  const getInvoiceExample = (prefix: string, digitCount: number): string => {
    const base = prefix || 'INV-'
    const d1 = padSequenceNumber(1, digitCount)
    return `eg: ${base}${d1}`
  }

  const getSaleInvoiceExample = (prefix: string, digitCount: number): string => {
    const base = prefix ?? DEFAULT_SALE_INVOICE_PREFIX
    const d1 = padSequenceNumber(1, digitCount)
    return `eg: ${base}${d1}`
  }

  const getChallanExample = (prefix: string, digitCount: number): string => {
    const base = prefix || 'CH-'
    const d1 = padSequenceNumber(1, digitCount)
    const d2 = padSequenceNumber(2, digitCount)
    return `eg: ${base}${d1}`
  }

  useEffect(() => {
    fetchData()
  }, [])
  const fetchData = async () => {
    getStoreTnc().then((response) => {
      if (response.success) {
        const tncData = response.data
        if (tncData) {
          setId(tncData.id)
          setBankAccountDetails(tncData.bankAccountDetails || '')
          setInvoiceTnc(tncData.invoiceTnc || '')
          setSaleInvoiceTnc(tncData.saleInvoiceTnc || '')
          setChallanTnc(tncData.challanTnc || '')
          setAgreementTnc(tncData.agreementTnc || '')
          setQuotationTnc(tncData.quotationTnc || '')
          setNonGstBankAccount(tncData.nonGstBankAccount || '')
          setInvoiceTitle(tncData.invoiceTitle || '')
          setChallanFormat(tncData.challanFormat ?? 'FORMAT_2027')

          // Initialize fields from configProperties if available
          if (tncData.configProperties) {
            const configProps = tncData.configProperties
            // Handle string fields (including empty strings)
            setOutwardChallanPrefix(configProps.outwardChallanPrefix ?? '')
            setInwardChallanPrefix(configProps.inwardChallanPrefix ?? '')
            setInvoiceNoPrefix(configProps.invoiceNoPrefix ?? '')
            setSaleInvoiceNoPrefix(
              configProps.saleInvoiceNoPrefix ?? DEFAULT_SALE_INVOICE_PREFIX
            )
            if (
              configProps.invoiceNoLength !== undefined &&
              configProps.invoiceNoLength !== null
            ) {
              const v = Number(configProps.invoiceNoLength)
              if ((NO_DIGIT_OPTIONS as readonly number[]).includes(v)) {
                setInvoiceNoLength(v)
              }
            }
            if (
              configProps.saleInvoiceNoLength !== undefined &&
              configProps.saleInvoiceNoLength !== null
            ) {
              const v = Number(configProps.saleInvoiceNoLength)
              if ((NO_DIGIT_OPTIONS as readonly number[]).includes(v)) {
                setSaleInvoiceNoLength(v)
              }
            }
            if (
              configProps.challanNoLength !== undefined &&
              configProps.challanNoLength !== null
            ) {
              const v = Number(configProps.challanNoLength)
              if ((NO_DIGIT_OPTIONS as readonly number[]).includes(v)) {
                setChallanNoLength(v)
              }
            }
            setUpiId(configProps.upiId ?? '')
            setUpiId2(configProps.upiId2 ?? '')
            // Handle number fields (use default if undefined)
            if (configProps.sgst !== undefined && configProps.sgst !== null) {
              setSgst(configProps.sgst)
            }
            if (configProps.cgst !== undefined && configProps.cgst !== null) {
              setCgst(configProps.cgst)
            }
            if (
              configProps.ironRate !== undefined &&
              configProps.ironRate !== null &&
              !Number.isNaN(Number(configProps.ironRate))
            ) {
              setIronRate(Number(configProps.ironRate))
            } else {
              setIronRate('')
            }
            setSelectedFont(parseSelectedFont(configProps.selectedFont))
            setFontSizeDeviation(
              parseFontSizeDeviation(configProps.fontSizeDeviation)
            )
          }
        }
      } else {
        dispatchAction(
          showAlertAction({
            message: response.message,
            severity: 'error',
          })
        )
      }
    })
  }

  // Initialize from userData.config if available (fallback for existing values when API doesn't have them)
  useEffect(() => {
    if (data?.config && !outwardChallanPrefix && !inwardChallanPrefix && !invoiceNoPrefix && !upiId && !upiId2) {
      // Only initialize if fields are empty (not yet loaded from API)
      if (data.config.outwardChallanPrefix) {
        setOutwardChallanPrefix(data.config.outwardChallanPrefix)
      }
      if (data.config.inwardChallanPrefix) {
        setInwardChallanPrefix(data.config.inwardChallanPrefix)
      }
      if (data.config.invoiceNoPrefix) {
        setInvoiceNoPrefix(data.config.invoiceNoPrefix)
      }
      if (data.config.upiId) {
        setUpiId(data.config.upiId)
      }
      if (data.config.upiId2) {
        setUpiId2(data.config.upiId2)
      }
      if (data.config.sgst) {
        setSgst(data.config.sgst)
      }
      if (data.config.cgst) {
        setCgst(data.config.cgst)
      }
      // Note: challanFormat is not in userData.config, so we keep the default
    }
  }, [data])

  const handleSubmit = async () => {
    const ironRatePart =
      ironRate === '' || ironRate === null || ironRate === undefined
        ? {}
        : (() => {
            const n = typeof ironRate === 'number' ? ironRate : Number(ironRate)
            return Number.isNaN(n) ? {} : { ironRate: n }
          })()

    const data = {
      id,
      bankAccountDetails,
      invoiceTnc,
      saleInvoiceTnc,
      challanTnc,
      agreementTnc,
      quotationTnc,
      invoiceTitle,
      nonGstBankAccount,
      challanFormat: challanFormat,
      configProperties: {
        outwardChallanPrefix,
        inwardChallanPrefix,
        invoiceNoPrefix,
        invoiceNoLength,
        saleInvoiceNoPrefix,
        saleInvoiceNoLength,
        challanNoLength,
        upiId,
        upiId2,
        sgst,
        cgst,
        selectedFont,
        fontSizeDeviation,
        ...ironRatePart,
      },
    }

    saveStoreTnc(data).then((response) => {
      if (response.success) {
        dispatchAction(
          showAlertAction({
            message: response.message,
            severity: 'success',
          })
        )
        fetchData()
      } else {
        dispatchAction(
          showAlertAction({
            message: response.message,
            severity: 'error',
          })
        )
      }
    })
  }

  return (
    <>
      <Helmet>
        <title>Manage Parties</title>
      </Helmet>
      <PageTitleWrapper>
        <TnCHeader />
      </PageTitleWrapper>
      <PageContainerWrapper>
        <Box sx={{ position: 'relative', minHeight: '100%', pb: 10 }}>
          <Box p={4}>
            <Grid container spacing={2}>
              {/* New Fields at the top */}
              <Grid item xs={12} md={6}>
                <Typography mt={0} mb={2} variant='h4'>Invoice Settings</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      label="Invoice No Prefix"
                      fullWidth
                      value={invoiceNoPrefix}
                      onChange={(e) => setInvoiceNoPrefix(e.target.value)}
                      helperText={getInvoiceExample(invoiceNoPrefix, invoiceNoLength)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="invoice-no-digits-label">Invoice No digits</InputLabel>
                      <Select
                        labelId="invoice-no-digits-label"
                        label="Invoice No digits"
                        value={invoiceNoLength}
                        onChange={(e) =>
                          setInvoiceNoLength(Number(e.target.value) as number)
                        }
                      >
                        {NO_DIGIT_OPTIONS.map((d) => (
                          <MenuItem key={d} value={d}>
                            {d}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <CustomNumberField
                      sx={{ mt: 0 }}
                      name="sgst"
                      maxLength={4}
                      value={sgst}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setSgst(event.target.valueAsNumber)
                      }}
                      size="small"
                      label="SGST Rate(%)"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CustomNumberField
                      sx={{ mt: 0 }}
                      name="cgst"
                      maxLength={4}
                      value={cgst}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setCgst(event.target.valueAsNumber)
                      }}
                      size="small"
                      label="CGST Rate(%)"
                      fullWidth
                    />
                  </Grid>
                </Grid>
                 <TextField
                  sx={{ mt: 5 }}
                  label="Bank Account Details(for GST invoice)"
                  fullWidth
                  multiline
                  rows={4}
                  value={bankAccountDetails}
                  onChange={(e) => setBankAccountDetails(e.target.value)}
                />
                <TextField
                  sx={{ mt: 4 }}
                  label="Bank Account Details(for Non GST invoice)"
                  fullWidth
                  multiline
                  rows={4}
                  value={nonGstBankAccount}
                  onChange={(e) => setNonGstBankAccount(e.target.value)}
                />
                 <Grid container spacing={2}>
                  <Grid item xs={6} md={6}>
                <TextField
                  sx={{ mt: 2 }}
                  size="small"
                  label="UPI ID (for GST invoice)"
                  fullWidth
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                />
                </Grid>
                <Grid item xs={6} md={6}>
                <TextField
                  sx={{ mt: 2 }}
                  size="small"
                  label="UPI ID (for Non GST invoice)"
                  fullWidth
                  value={upiId2}
                  onChange={(e) => setUpiId2(e.target.value)}
                />
                </Grid></Grid>
                <TextField
                  sx={{ mt: 4 }}
                  label="Invoice T&C"
                  fullWidth
                  multiline
                  rows={4}
                  value={invoiceTnc}
                  onChange={(e) => setInvoiceTnc(e.target.value)}
                />


                <TextField
                  sx={{ mt: 2 }}
                  size='small'
                  label="Invoice Title"
                  fullWidth
                  multiline
                  rows={1}
                  value={invoiceTitle}
                  onChange={(e) => setInvoiceTitle(e.target.value)}
                />

              </Grid>
              <Grid item xs={12} md={6}>
                <Typography mt={0} mb={2} variant='h4'>Challan Settings</Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      size="small"
                      label="Delivery Challan Prefix"
                      fullWidth
                      value={outwardChallanPrefix}
                      onChange={(e) => setOutwardChallanPrefix(e.target.value)}
                      helperText={getChallanExample(outwardChallanPrefix, challanNoLength)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      size="small"
                      label="Receiving Challan Prefix"
                      fullWidth
                      value={inwardChallanPrefix}
                      onChange={(e) => setInwardChallanPrefix(e.target.value)}
                      helperText={getChallanExample(inwardChallanPrefix, challanNoLength)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="challan-no-digits-label">Challan No digits</InputLabel>
                      <Select
                        labelId="challan-no-digits-label"
                        label="Challan No digits"
                        value={challanNoLength}
                        onChange={(e) =>
                          setChallanNoLength(Number(e.target.value) as number)
                        }
                      >
                        {NO_DIGIT_OPTIONS.map((d) => (
                          <MenuItem key={d} value={d}>
                            {d}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      size="small"
                      options={['FORMAT_2027', 'FORMAT_2026', 'FORMAT_2022']}
                      value={challanFormat}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setChallanFormat(newValue)
                        }
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Challan Format" sx={{mb:0}} />
                      )}
                      sx={{mb:0}}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mt: -1.5 }}>
                
                      <Link
                        href="https://scaffoldingapp.com/pb-doc-service/local-machine-testing/temporary/CHALLAN-404924e4-bd08-4d93-a60b-9f69319e1b32.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'blue', textDecoration: 'underline' }}
                      >
                        FORMAT_2027
                      </Link>
                      {' '}
                      <Link
                        href="https://scaffoldingapp.com/pb-doc-service/2026/temporary/CHALLAN-6c44ff28-3835-4150-93a8-f3b4626397d7.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'blue', textDecoration: 'underline', ml:2 }}
                      >
                        FORMAT_2026
                      </Link>
                      {' '}
                      <Link
                        href="https://scaffoldingapp.com/pb-doc-service/2026/temporary/CHALLAN-929cc5db-1a15-4a6d-b4fd-3dc948f88f6e.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'blue', textDecoration: 'underline', ml:2 }}
                      >
                        FORMAT_2022
                      </Link>
                    </Typography>
                  </Grid>
                </Grid>

                <TextField
                  sx={{ mt: 2 }}
                  label="Challan T&C"
                  fullWidth
                  multiline
                  rows={4}
                  value={challanTnc}
                  onChange={(e) => setChallanTnc(e.target.value)}
                />
                 <TextField
                  sx={{ mt: 4 }}
                  label="Agreement T&C"
                  fullWidth
                  multiline
                  rows={4}
                  value={agreementTnc}
                  onChange={(e) => setAgreementTnc(e.target.value)}
                />
                <CustomNumberField
                  sx={{ mt: 2 }}
                  name="ironRate"
                  maxLength={12}
                  value={ironRate === '' ? '' : ironRate}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const v = event.target.value
                    if (v === '') {
                      setIronRate('')
                      return
                    }
                    const n = event.target.valueAsNumber
                    setIronRate(Number.isNaN(n) ? '' : n)
                  }}
                  size="small"
                  label="Iron Rate/Kg (optional)"
                  fullWidth
                  inputProps={{ step: 'any', min: 0 }}
                />
                <TextField
                  sx={{ mt: 4 }}
                  label="Quotation T&C"
                  fullWidth
                  multiline
                  rows={4}
                  value={quotationTnc}
                  onChange={(e) => setQuotationTnc(e.target.value)}
                />
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="selected-font-label">Select Font</InputLabel>
                      <Select
                        labelId="selected-font-label"
                        label="Select Font"
                        value={selectedFont}
                        onChange={(e) =>
                          setSelectedFont(e.target.value as InvoiceFont)
                        }
                      >
                        {FONT_OPTIONS.map((font) => (
                          <MenuItem key={font} value={font}>
                            {font}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="font-size-deviation-label">
                        Font Size Change
                      </InputLabel>
                      <Select
                        labelId="font-size-deviation-label"
                        label="Font Size Change"
                        value={fontSizeDeviation}
                        onChange={(e) =>
                          setFontSizeDeviation(
                            Number(e.target.value) as FontSizeDeviation
                          )
                        }
                      >
                        {FONT_SIZE_DEVIATION_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

              </Grid>

              <Grid item xs={12} mt={3}>
                <Typography mt={0} mb={2} variant="h4">
                  Sale Invoice Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          size="small"
                          label="Sale Invoice No Prefix"
                          fullWidth
                          value={saleInvoiceNoPrefix}
                          onChange={(e) =>
                            setSaleInvoiceNoPrefix(e.target.value)
                          }
                          helperText={getSaleInvoiceExample(
                            saleInvoiceNoPrefix,
                            saleInvoiceNoLength
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl size="small" fullWidth>
                          <InputLabel id="sale-invoice-no-digits-label">
                            Sale Invoice No digits
                          </InputLabel>
                          <Select
                            labelId="sale-invoice-no-digits-label"
                            label="Sale Invoice No digits"
                            value={saleInvoiceNoLength}
                            onChange={(e) =>
                              setSaleInvoiceNoLength(
                                Number(e.target.value) as number
                              )
                            }
                          >
                            {NO_DIGIT_OPTIONS.map((d) => (
                              <MenuItem key={d} value={d}>
                                {d}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Sale Invoice T&C"
                      fullWidth
                      multiline
                      rows={4}
                      value={saleInvoiceTnc}
                      onChange={(e) => setSaleInvoiceTnc(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {/* Sticky Submit Button */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'white',
              borderTop: '1px solid',
              borderColor: 'divider',
              p: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              zIndex: 10,
            }}
          >
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                backgroundColor: 'green',
                '&:hover': {
                  backgroundColor: '#45a049',
                },
                mr: 2
              }}
            >
              SUBMIT
            </Button>
          </Box>
        </Box>
      </PageContainerWrapper>
    </>
  )
}

export default TnCView
